import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { awardsApi } from '@/api/awards';
import type { AwardTypePayload, CreateAwardRequest } from '@/api/types';

export const awardKeys = {
  all: ['awards'] as const,
  forCompetition: (competitionId: string) => [...awardKeys.all, 'competition', competitionId] as const,
  typesForCompetition: (competitionId: string) => [...awardKeys.all, 'types', competitionId] as const,
};

/** The awards the organizers defined for a competition. */
export function useAwardTypes(competitionId: string | undefined) {
  return useQuery({
    queryKey: awardKeys.typesForCompetition(competitionId ?? ''),
    queryFn: () => awardsApi.listTypes(competitionId as string),
    enabled: Boolean(competitionId),
  });
}

export function useSaveAwardType(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: AwardTypePayload }) =>
      id ? awardsApi.updateType(id, payload) : awardsApi.createType(competitionId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: awardKeys.typesForCompetition(competitionId) }),
  });
}

export function useDeleteAwardType(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => awardsApi.removeType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: awardKeys.typesForCompetition(competitionId) });
      queryClient.invalidateQueries({ queryKey: awardKeys.forCompetition(competitionId) });
    },
  });
}

export function useAwards(competitionId: string | undefined) {
  return useQuery({
    queryKey: awardKeys.forCompetition(competitionId ?? ''),
    queryFn: () => awardsApi.listForCompetition(competitionId as string),
    enabled: Boolean(competitionId),
  });
}

export function useCreateAward(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Omit<CreateAwardRequest, 'competitionId'>) =>
      awardsApi.create({ ...payload, competitionId }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: awardKeys.forCompetition(competitionId) }),
  });
}

export function useDeleteAward(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => awardsApi.remove(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: awardKeys.forCompetition(competitionId) }),
  });
}
